import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

// Per-viewer conveniences (theme, mute). localStorage is fine here: nothing sensitive, and the
// app works the same when storage is blocked.

export type ThemePref = 'system' | 'light' | 'dark';

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, v: string) {
  try {
    window.localStorage.setItem(key, v);
  } catch {
    /* ignore */
  }
}

interface Prefs {
  theme: ThemePref;
  setTheme(t: ThemePref): void;
  /** The theme actually showing (system resolved). */
  resolvedTheme: 'light' | 'dark';
  muted: boolean;
  setMuted(m: boolean): void;
}

const PrefsContext = createContext<Prefs | null>(null);

function systemDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function PrefsProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePref>(() => {
    const t = read('popcart.backoffice.theme');
    return t === 'light' || t === 'dark' ? t : 'system';
  });
  const [sysDark, setSysDark] = useState(systemDark);
  const [muted, setMutedState] = useState(() => read('popcart.backoffice.muted') === '1');

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const on = () => setSysDark(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
  }, [theme]);

  const setTheme = useCallback((t: ThemePref) => {
    setThemeState(t);
    write('popcart.backoffice.theme', t);
  }, []);
  const setMuted = useCallback((m: boolean) => {
    setMutedState(m);
    write('popcart.backoffice.muted', m ? '1' : '0');
  }, []);

  const resolvedTheme: 'light' | 'dark' = theme === 'system' ? (sysDark ? 'dark' : 'light') : theme;
  const value = useMemo(() => ({ theme, setTheme, resolvedTheme, muted, setMuted }), [theme, setTheme, resolvedTheme, muted, setMuted]);
  return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>;
}

export function usePrefs(): Prefs {
  const v = useContext(PrefsContext);
  if (!v) throw new Error('usePrefs outside PrefsProvider');
  return v;
}
